import * as React from 'react'

import { Maybe } from '@zaptic-external/ts-monads'
import i18next from 'i18next'
import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { RedesignedUnitSubjectPicker } from '../report/RedesignedUnitSubjectPicker'
import { SubjectPickerPage } from '../report/SubjectPickerPage'
import type { BarcodeResult } from '../unit/barcodeTypes'
import type { KnownUnit } from '../unit/unit'
import { cancelPromiseManagers, createPromiseManager } from '../util/PromiseManager'
import { initialPromiseState, promiseStateMaybeGet, promiseStateRender, type PromiseState } from '../util/PromiseState'
import { createStateLens } from '../util/react'
import { fetchUnitsForSubjectPicker, type UnitsForSubjectPicker } from './SubjectType'

interface Report {
    id: Flows.ReportId
    type: 'action' | 'task' | 'training' | 'guide'
    flow: Flows.Flow
    flowTitle: string
    dueDate?: Date
    assignee?: { teams: string[] }
    procedure: {
        id: number
        name: string
    }
    parentSubjectUnitId?: number
}

interface Props {
    deps: CommonDeps
    onBack: () => void
    reportState: PromiseState<Report>
    renderFooter: () => React.ReactElement<unknown>
    fetchUnitsByIds: (unitIds: number[]) => Promise<KnownUnit[]>
    fetchUnitsByType: (unitTypes: number[]) => Promise<KnownUnit[]>
    fetchUnitTypeName: (unitTypeId: number) => Promise<Maybe<string>>
    saveUnitSubject: (reportId: Flows.ReportId, flow: Flows.Flow, unit: KnownUnit) => Promise<void>
    routeToProcedurePage: (procedure: number) => void
    barcodeScanForUnits?: (units: KnownUnit[]) => Promise<BarcodeResult>
    renderCachedImage: RenderCachedImage
}

interface State {
    unitsForSubjectPickerState: PromiseState<UnitsForSubjectPicker>
    subjectUnitId: number
}

export class ExecutionSummaryPage extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {
        unitsForSubjectPickerState: initialPromiseState,
        subjectUnitId: null,
    }

    private promiseManagers = {
        unitsForSubjectPicker: createPromiseManager(
            () => this.props.deps,
            createStateLens(this, 'unitsForSubjectPickerState')
        ),
    }

    public componentDidMount() {
        this.fetchUnits()
    }

    public componentDidUpdate() {
        this.fetchUnits()
    }

    public componentWillUnmount() {
        cancelPromiseManagers(this.promiseManagers)
    }

    public fetchUnits() {
        // Only fetch once
        if (this.state.unitsForSubjectPickerState.status !== 'not-triggered') return

        // Do not fetch until the report has been fetched
        const report = promiseStateMaybeGet(this.props.reportState)
        if (report == null) return

        this.promiseManagers.unitsForSubjectPicker.trigger(() =>
            fetchUnitsForSubjectPicker(
                report.flow.subjectType,
                this.props.fetchUnitsByType,
                this.props.fetchUnitsByIds,
                report.parentSubjectUnitId
            )
        )
    }

    private saveUnitSubject = async (report: Report) => {
        const { units } = this.promiseManagers.unitsForSubjectPicker.mustGet()
        const unit = units.find((u) => u.id === this.state.subjectUnitId)
        await this.props.saveUnitSubject(report.id, report.flow, unit)
    }

    private tryRender = (): JSX.Element => {
        return promiseStateRender(this.props.deps, this.props.reportState, (report) =>
            this.promiseManagers.unitsForSubjectPicker.render(({ units, ancestors }) => {
                return (
                    <SubjectPickerPage name={report.flowTitle} deps={this.props.deps} onBackClick={this.props.onBack}>
                        <RedesignedUnitSubjectPicker
                            deps={this.props.deps}
                            onStart={() => this.saveUnitSubject(report)}
                            startLabel={i18next.t('appcore:common.start')}
                            maybeSelectedUnitId={Maybe.fromOptional(this.state.subjectUnitId)}
                            unitTypeId={report.flow.subjectType.unitType}
                            onSelect={(subjectUnitId) => this.setState({ subjectUnitId })}
                            fetchUnitTypeName={this.props.fetchUnitTypeName}
                            units={units}
                            ancestors={ancestors}
                            renderCachedImage={this.props.renderCachedImage}
                            barcodeScanForUnits={this.props.barcodeScanForUnits}
                        />
                    </SubjectPickerPage>
                )
            })
        )
    }
}
